<template>
  <v-dialog v-model="dialog" width="800" :disabled="!hasPermission('menu_schema_edit')">
    <template #activator="{ on }">
      <icon-button
          v-on="on"
          icon="query_builder"
          color="teal"
          cls="mr-0 ml-0"
          permission="menu_schema_edit"
          :tooltip="{ bottom: true, attach: true}"
      >
        {{ 'app.meals_management.manage_meals' | trans }}
      </icon-button>
    </template>

    <v-card class="bg-whitesmoke">
      <v-card-title class="grey lighten-2" primary-title>
        <h3 class="headline mb-0 mt-0">
          {{ 'app.meals_management.manage_meals' | trans }}
        </h3>
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-container grid-list-md>
            <v-layout row wrap>
              <v-flex xs6>
                <v-select
                    v-model="selectedDay"
                    :items="days"
                    :label="'app.meals_management.select_day' | trans"
                    :disabled="manageAllDays"
                    item-text="label"
                    item-value="value"
                    return-object
                    @change="selectedDayChange()"
                    solo
                ></v-select>
              </v-flex>
              <v-flex xs6>
                <v-checkbox
                    :color="`success`"
                    v-model="manageAllDays"
                    :label="'app.meals_management.manage_all_days' | trans"
                ></v-checkbox>
              </v-flex>
            </v-layout>
            <v-divider></v-divider>
            <v-layout row wrap>
              <v-flex xs12>
                <v-alert class="alert" type="warning" :value="areMealsDifferent()">
                  <div style="float:left">{{ 'app.meals_management.all_plans_not_the_same' | trans }}</div>
                </v-alert>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex xs4 offset-xs7>
                <v-radio-group v-model="inputMode" row>
                  <v-radio :label="`%`" value="percentage" :color="`success`"></v-radio>
                  <v-radio :class="`kcal-input-mode-radio`" :label="`app.energy` | trans" value="energy"
                           :color="`success`"></v-radio>
                </v-radio-group>
              </v-flex>
            </v-layout>
            <v-layout row wrap v-for="(meal, index) in meals"
                      :key="meal.id">
              <v-flex xs5>
                <v-text-field
                    required
                    :label="'app.name' | trans"
                    v-model="meal.name"
                    :rules="rules.name"
                ></v-text-field>
              </v-flex>
              <v-flex xs2>
                  <v-text-field
                      readonly
                      prepend-icon="access_time"
                      :label="'app.hour' | trans"
                      v-model="meal.time"
                      @click="openTimePicker(meal)"
                      :rules="rules.time"
                  ></v-text-field>
              </v-flex>
              <v-flex xs2>
                <v-text-field
                    required
                    :disabled="inputMode === 'energy'"
                    :label="'app.energy_percentage' | trans"
                    :value="meal.energy_percentage"
                    type="number"
                    suffix="%"
                    step="1"
                    max="100"
                    min="1"
                    :rules="rules.energy_percentage"
                    @input="energyPercentageInput(meal, $event, index)"
                >
                </v-text-field>
              </v-flex>
              <v-flex xs2>
                <v-text-field
                    required
                    :disabled="inputMode === 'percentage'"
                    :label="'app.energy' | trans"
                    :value="meal.energy"
                    type="number"
                    suffix="kcal"
                    :rules="rules.energy"
                    @input="energyInput(meal, $event, index)"
                >
                </v-text-field>
              </v-flex>
              <v-flex xs1>
                <v-tooltip top>
                  <template #activator="{ on }">
                    <v-btn
                        v-on="on"
                        flat
                        icon
                        color="red"
                        class="mr-0 ml-0"
                        @click="removeMeal(index)"
                        :disabled="meals.length === 1"
                    >
                      <v-icon small>delete</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ 'app.delete' | trans }}</span>
                </v-tooltip>
              </v-flex>
            </v-layout>
            <v-divider></v-divider>
            <v-layout row wrap>
              <v-flex xs3>
                <v-btn small flat color="success"
                       @click="addMeal()">
                  <v-icon dark>add</v-icon>
                  {{ 'app.add_meal' | trans }}
                </v-btn>
              </v-flex>
              <v-flex offset-xs3 xs3>
                <p class="text-xs-right">
                  {{ totalEnergyPercentage | round }} / 100 %
                </p>
              </v-flex>
              <v-flex xs3>
                <p class="text-xs-right">
                  {{ totalEnergy | round }} / {{ this.menu.schema.desiredEnergy.amount }} kcal
                </p>
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
        <v-dialog
            v-model="timePickerMenu"
            lazy
            hide-overlay
            full-width
            max-width="290px"
            min-width="290px"
        >
          <v-time-picker format="24hr" v-model="time"></v-time-picker>
        </v-dialog>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" flat @click="close">
          {{ 'app.close' | trans }}
        </v-btn>
        <v-btn color="primary" @click="save" :disabled="!valid" :loading="loading">
          {{ 'app.save' | trans }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {mapGetters, mapState} from 'vuex';
import {flatten, uniqWith} from 'lodash';
import {planValidation} from '../validations/plan';
import {capitalize, formatDate} from "../filters";
import {Energy} from "../model/energy.model";
import {trans} from "../app";
import {planFactory} from "../factory/plan.factory";
import uuid from 'uuid4';
import {round} from "../utils/money";
import {CARBO, FATS, KCAL, PROTEIN} from "../services/NutrientDetailsProvider";

export default {
  data: () => ({
        time: null,
        timePickerMenu: false,
        timeEditedMeal: null,
        dialog: false,
        loading: false,
        valid: false,
        manageAllDays: true,
        selectedDay: null,
        meals: [],
        inputMode: 'energy',
        removedMeals: [],
      }
  ),
  watch: {
    dialog(value) {
      if (value === true) {
        this.manageAllDays = true;
        this.selectedDay = this.days[0];
        this.setMeals();
      } else {
        this.meals = [];
        this.selectedDay = null;
        this.inputMode = 'energy';
        this.removedMeals = [];
      }
    },
    timePickerMenu(timePickerMenu) {
      if (!timePickerMenu && this.timeEditedMeal) {
        this.timeEditedMeal.time = this.time;
        this.time = null;
        this.timeEditedMeal = null;
        this.sortMeals();
      }
    }
  },

  computed: {
    ...mapState(['dictionaries', 'menu']),
    ...mapGetters(['hasPermission', 'configuration']),

    rules() {
      return planValidation;
    },
    days() {
      let dayNumber = 0;
      return this.menu.schema.days.map(day => ({
        label: `${formatDate(day.date)} ${capitalize(formatDate(day.date, 'dddd'))} (${trans('app.day')} ${++dayNumber})`,
        value: day.id,
      }));
    },
    totalEnergy() {
      return this.meals.reduce((totalEnergy, meal) => {
        return totalEnergy + meal.energy;
      }, 0);
    },
    totalEnergyPercentage() {
      return this.meals.reduce((totalEnergyPercentage, meal) => {
        return totalEnergyPercentage + meal.energy_percentage;
      }, 0);
    }
  },

  methods: {
    open() {
      this.dialog = true;
    },

    save() {
      this.menu.schema.days.map((day) => {
        const isSelectedDay = day.id === this.selectedDay.value
        if (this.manageAllDays || isSelectedDay) {
          day = this.updateDay(day, isSelectedDay);
        }

        return day;
      });

      if (this.manageAllDays && this.totalEnergy !== this.menu.schema.desiredEnergy.amount) {
        this.updateDesiredEnergy();
      }

      this.close();
    },

    updateDay(day, isSelectedDay) {
      //remove plans
      if (isSelectedDay) {
        const originalMeals = this.meals.map(meal => meal.originalName).filter(originalName => originalName !== '');
        day.plans.forEach((dayPlan) => {
          if (originalMeals.includes(dayPlan.name) === false) {
            day.removePlan(dayPlan.id);
          }
        });
      } else {
        day.plans.forEach((dayPlan) => {
          if (this.removedMeals.includes(dayPlan.name)) {
            day.removePlan(dayPlan.id);
          }
        });
      }

      //update existing plans
      this.meals.forEach((meal) => {
        day.plans.map(dayPlan => {
          if (dayPlan.name === meal.originalName) {
            dayPlan.name = meal.name;
            if (dayPlan.time !== meal.time) {
              dayPlan.time = meal.time;
            }
            if (dayPlan.prescribedEnergy.amount !== meal.energy) {
              dayPlan.prescribedEnergy = new Energy(meal.energy);
            }
          }
        })
      });

      //add new plans
      this.meals.forEach((meal) => {
        if (meal.id === null || day.hasPlan(meal.name) === false) {
          day.addPlan(planFactory.create(
              uuid(),
              meal.name,
              meal.energy,
              meal.time,
              '',
              [],
              [],
              day.date
          ));
        }
      })

      return day;
    },

    updateDesiredEnergy() {
      if (this.configuration.mode === 'menu') {
        this.menu.schema.desiredEnergy = this.totalEnergy;
      }
      const nutrientPercentage = this.getNutrientPercentage();
      if (this.configuration.mode !== 'menu') {
        this.menu.schema.desiredEnergy = this.totalEnergy;
      }
      this.updatePrescribedNutrients(nutrientPercentage);
    },

    updatePrescribedNutrients(nutrientPercentage) {
        const desiredDietEnergy = this.menu.schema.desiredEnergy.amount;
        this.menu.schema.prescribedNutrients[PROTEIN] = parseFloat(((parseFloat(nutrientPercentage[PROTEIN]) * desiredDietEnergy / 100) / 4).toFixed(2)).toString() | 0;
        this.menu.schema.prescribedNutrients[FATS] = parseFloat(((parseFloat(nutrientPercentage[FATS]) * desiredDietEnergy / 100) / 9).toFixed(2)).toString() | 0;
        this.menu.schema.prescribedNutrients[CARBO] = parseFloat(((parseFloat(nutrientPercentage[CARBO]) * desiredDietEnergy / 100) / 4).toFixed(2)).toString() | 0;
        this.menu.schema.prescribedNutrients[KCAL] = desiredDietEnergy;
    },

    getNutrientPercentage() {
      if (this.configuration.mode === 'menu') {
        return this.configuration.nutrients_percentage;
      } else {
        const protein = this.menu.schema.prescribedNutrients[PROTEIN];
        const fats = this.menu.schema.prescribedNutrients[FATS];
        const carbo = this.menu.schema.prescribedNutrients[CARBO];
        let nutrientPercentage = {};
        nutrientPercentage[PROTEIN] = typeof protein !== 'undefined' ? Math.round(this.calculatePercentageFromGrams(protein, PROTEIN)).toString() : '0';
        nutrientPercentage[FATS] = typeof fats !== 'undefined' ? Math.round(this.calculatePercentageFromGrams(fats, FATS)).toString() : '0';
        nutrientPercentage[CARBO] = typeof carbo !== 'undefined' ? Math.round(this.calculatePercentageFromGrams(carbo, CARBO)).toString() : '0';
        return nutrientPercentage;
      }
    },

    calculatePercentageFromGrams(value, externalId) {
      const gramToEnergy = {
        1: 4,
        2: 9,
        3: 4,
      }
      return (value / this.menu.schema.desiredEnergy.amount * 100) * gramToEnergy[externalId];
    },

    close() {
      this.dialog = false;
      setTimeout(() => {
        this.$emit('close');
      }, 100);
    },

    energyPercentageInput(meal, percentage, index) {
      if (percentage < 0.01) {
        percentage = 0.01;
      }
      let value = round(percentage * this.menu.schema.desiredEnergy.amount / 100, 0);
      this.$nextTick(() => {
        this.meals[index].energy = value;
        this.meals[index].energy_percentage = Number(percentage);
        if (this.totalEnergyPercentage === 100) {
          const difference = round(this.menu.schema.desiredEnergy.amount - this.totalEnergy, 0);
          const lastIndex = this.meals.length - 1;
          this.meals[lastIndex].energy = round(this.meals[lastIndex].energy + difference, 0);
        }
      });

      return value;
    },

    energyInput(meal, energy, index) {
      if (energy < 1) {
        energy = 1;
      }
      let value = round(energy / this.menu.schema.desiredEnergy.amount * 100, 2);

      this.$nextTick(() => {
        this.meals[index].energy_percentage = value;
        this.meals[index].energy = Number(energy);
        if (this.totalEnergy === this.menu.schema.desiredEnergy.amount) {
          const difference = round(100 - round(this.totalEnergyPercentage, 2), 2);
          const lastIndex = this.meals.length - 1;
          this.meals[lastIndex].energy_percentage = round(this.meals[lastIndex].energy_percentage + difference, 2);
        }
      });

      return value;
    },

    selectedDayChange() {
      this.setMeals();
    },

    setMeals() {
      let days = this.menu.schema.days;
      days = days.filter(day => day.id === this.selectedDay.value);
      let meals = flatten(days.map(d => d.plans)).map(p => {
        let energy = parseFloat(p.prescribedEnergy.amount);
        return {
          name: p.name,
          originalName: p.name,
          time: p.time,
          energy: energy,
          energy_percentage: round(energy / this.menu.schema.desiredEnergy.amount * 100, 2),
          id: p.id,
        }
      })


      this.meals = uniqWith(meals, (a, b) => {
        return `${a.name}` === `${b.name}`;
      });

      const difference = round(100 - round(this.totalEnergyPercentage, 2), 2);
      this.meals = this.meals.map((meal, index) => {
        if (index === this.meals.length - 1 && this.totalEnergy === this.menu.schema.desiredEnergy.amount) {
          meal.energy_percentage = round(meal.energy_percentage + difference, 2);
        }
        return meal;
      })

      this.sortMeals();
    },

    sortMeals() {
      this.meals.sort((a, b) => {
        return moment(a.time, 'HH:mm').unix() - moment(b.time, 'HH:mm').unix();
      });
    },

    findModelDay() {
      let scores = {};
      this.menu.schema.days.forEach((day) => {
        this.menu.schema.days.filter(otherDay => otherDay.id !== day.id)
            .forEach((otherDay) => {
              const dayPlans = this.getComparableDayPlans(day);
              const otherDayPlans = this.getComparableDayPlans(otherDay);
              if (dayPlans === otherDayPlans) {
                if (typeof scores[day.id] !== 'undefined') {
                  scores[day.id] = ++scores[day.id]
                } else {
                  scores[day.id] = 1;
                }
              }
            });
      });
      const highestScore = Object.entries(scores).map(score => {
        return {
          id: score[0],
          value: score[1]
        }
      }).sort((a, b) => {
        return b.value - a.value;
      })[0];

      if (typeof highestScore !== 'undefined') {
        return this.menu.schema.days.filter((day) => {
          return day.id === highestScore.id;
        })[0];
      }

      return null;
    },

    areMealsDifferent() {
      const modelDay = this.findModelDay();
      if (modelDay === null) {
        return true;
      }

      const modelDayPlans = this.getComparableDayPlans(modelDay);

      let areDifferent = false;
      this.menu.schema.days.forEach((day) => {
        const dayPlans = this.getComparableDayPlans(day);
        if (modelDayPlans !== dayPlans) {
          areDifferent = true;
        }
      });
      return areDifferent;
    },

    getComparableDayPlans(day) {
      return JSON.stringify(day.plans.map(dayPlan => dayPlan.name).sort())
    },

    removeMeal(index) {
      this.$confirmDanger(trans('app.meals_management.removal_confirmation')).then(() => {
        this.removedMeals.push(this.meals[index].originalName);
        this.meals.splice(index, 1);
      }).catch((error) => {
        this.sentryCaptureException(error);
      });
    },

    async addMeal() {
      this.meals.push({
        name: '',
        originalName: '',
        time: '',
        date: '',
        energy: 0,
        energy_percentage: round(0 / this.menu.schema.desiredEnergy.amount * 100, 2),
        id: null,
      });
      await this.$nextTick();
      this.$refs.form.validate();
    },
    openTimePicker(meal) {
      this.timePickerMenu = true;
      this.time = meal.time;
      this.timeEditedMeal = meal;
    }
  }
};
</script>
<style lang="scss" scoped>
.kcal-input-mode-radio {
  margin-left: 60px;
}
</style>
